/**
 * @file Phone / mobile validation helpers for forms.
 */

const PHONE_INPUT_PATTERN = /^[\d\s+\-().]*$/;

/** Strip formatting characters; keep leading + if present. */
export function normalizePhoneInput(value: string): string {
  return value.replace(/[\s\-().]/g, '');
}

/** Allow only characters commonly used in phone fields while typing. */
export function sanitizePhoneInput(value: string): string {
  return value.replace(/[^\d\s+\-().]/g, '');
}

export function isPhoneInputCharAllowed(value: string): boolean {
  return PHONE_INPUT_PATTERN.test(value);
}

export interface PhoneValidationResult {
  valid: boolean;
  message: string | null;
  /** Digits-only form suitable for API (India 10-digit or E.164 without +). */
  normalized: string;
}

/**
 * Validates mobile / phone numbers.
 * Accepts India mobiles (10 digits, 6–9 start) with optional +91 / 91 / 0 prefix,
 * or international numbers (8–15 digits, E.164-style).
 */
export function validateMobilePhone(value: string): PhoneValidationResult {
  const trimmed = value.trim();
  if (!trimmed) {
    return { valid: false, message: 'Phone number is required', normalized: '' };
  }

  if (!isPhoneInputCharAllowed(trimmed)) {
    return {
      valid: false,
      message: 'Phone number can only contain digits, spaces, +, -, and parentheses',
      normalized: '',
    };
  }

  const compact = normalizePhoneInput(trimmed);
  let digits = compact.startsWith('+') ? compact.slice(1) : compact;

  if (!/^\d+$/.test(digits)) {
    return {
      valid: false,
      message: 'Enter a valid mobile number (e.g. 9876543210 or +91 9876543210)',
      normalized: '',
    };
  }

  // India: +91 / 91 prefix
  if (digits.startsWith('91') && digits.length === 12) {
    digits = digits.slice(2);
  } else if (digits.startsWith('0') && digits.length === 11) {
    digits = digits.slice(1);
  }

  if (/^[6-9]\d{9}$/.test(digits)) {
    return { valid: true, message: null, normalized: digits };
  }

  // International (E.164 length, no leading zero on country/significant number)
  if (/^[1-9]\d{7,14}$/.test(digits)) {
    return { valid: true, message: null, normalized: digits };
  }

  return {
    valid: false,
    message: 'Enter a valid 10-digit mobile number (starts with 6–9) or use +91 prefix',
    normalized: '',
  };
}
